import { 
  Wrench, 
  Truck, 
  House, 
  ChatText, 
  NoteBlank, 
  EnvelopeSimple 
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { SupplierData, OrderFormData } from './types';

interface CreateOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isInternalAssignment: boolean;
  suppliers: SupplierData[];
  selectedSupplierId: string;
  onSupplierSelect: (supplierId: string) => void;
  form: OrderFormData;
  onFormChange: (form: OrderFormData) => void;
  showPreview: boolean;
  onPreviewToggle: () => void;
  onSubmit: () => void;
  buttonLabel?: string;
}

export function CreateOrderDialog({
  open,
  onOpenChange,
  isInternalAssignment,
  suppliers,
  selectedSupplierId,
  onSupplierSelect,
  form,
  onFormChange,
  showPreview,
  onPreviewToggle,
  onSubmit,
  buttonLabel = 'Skapa arbetsorder'
}: CreateOrderDialogProps) {
  const activeSuppliers = suppliers.filter(s => s.is_active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid="create-order-button">
          <Wrench size={14} className="mr-1" />
          {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Wrench size={18} />
            Skapa arbetsorder
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* Show supplier selection only for external assignments */}
          {!isInternalAssignment ? (
            <>
              {/* Supplier Selection */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Välj leverantör</Label>
                <Select
                  value={selectedSupplierId}
                  onValueChange={onSupplierSelect}
                >
                  <SelectTrigger className="h-9" data-testid="order-supplier-select">
                    <SelectValue placeholder="Välj leverantör för att auto-fylla..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeSuppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        <div className="flex items-center gap-2">
                          <Truck size={14} />
                          {supplier.name}
                          {supplier.email && (
                            <span className="text-xs text-slate-400">({supplier.email})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                    {activeSuppliers.length === 0 && (
                      <SelectItem value="none" disabled>Inga aktiva leverantörer</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Auto-filled supplier info */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Namn *</Label>
                  <Input
                    className="h-8 text-sm"
                    value={form.subcontractor_name}
                    onChange={(e) => onFormChange({ ...form, subcontractor_name: e.target.value })}
                    data-testid="order-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">E-post *</Label>
                  <Input
                    type="email"
                    className="h-8 text-sm"
                    value={form.subcontractor_email}
                    onChange={(e) => onFormChange({ ...form, subcontractor_email: e.target.value })}
                    data-testid="order-email-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Telefon</Label>
                  <Input
                    className="h-8 text-sm"
                    value={form.subcontractor_phone}
                    onChange={(e) => onFormChange({ ...form, subcontractor_phone: e.target.value })}
                    data-testid="order-phone-input"
                  />
                </div>
              </div>
            </>
          ) : (
            /* Internal assignment - no supplier needed */
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-800 mb-2">
                <House size={18} />
                <span className="font-medium">Intern arbetsorder</span>
              </div>
              <p className="text-sm text-blue-700">
                Ärendet är tilldelat internt. Arbetsordern blir ett underlag för föreningen att genomföra arbetet.
              </p>
            </div>
          )}

          {/* Due date */}
          <div className="space-y-1">
            <Label className="text-xs">Önskat slutdatum</Label>
            <Input
              type="date"
              className="h-8 text-sm w-48"
              value={form.due_date}
              onChange={(e) => onFormChange({ ...form, due_date: e.target.value })}
              data-testid="order-due-date-input"
            />
          </div>

          {/* Include history checkbox */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Checkbox
              id="include-history"
              checked={form.include_history}
              onCheckedChange={(checked) => onFormChange({ ...form, include_history: checked === true })}
              data-testid="include-history-checkbox"
            />
            <div className="flex-1">
              <label htmlFor="include-history" className="text-sm font-medium cursor-pointer">
                Inkludera ärendehistorik
              </label>
              <p className="text-xs text-slate-500">
                E-post och formulärkonversation struktureras automatiskt
              </p>
            </div>
            <ChatText size={20} className="text-slate-400" />
          </div>

          {/* Include internal notes checkbox */}
          <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <Checkbox
              id="include-internal-notes"
              checked={form.include_internal_notes}
              onCheckedChange={(checked) => onFormChange({ ...form, include_internal_notes: checked === true })}
              data-testid="include-internal-notes-checkbox"
            />
            <div className="flex-1">
              <label htmlFor="include-internal-notes" className="text-sm font-medium cursor-pointer text-emerald-800">
                Inkludera interna anteckningar
              </label>
              <p className="text-xs text-emerald-600">
                Visa gröna anteckningar för leverantören
              </p>
            </div>
            <NoteBlank size={20} className="text-emerald-500" />
          </div>

          {/* Preview toggle */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={showPreview ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={onPreviewToggle}
            >
              {showPreview ? 'Visa textvy' : 'Förhandsgranska HTML'}
            </Button>
            {form.include_history && (
              <span className="text-[10px] text-green-600">Auto-ifyllt med historik</span>
            )}
          </div>

          {/* Instructions - Text or HTML Preview */}
          {showPreview ? (
            <div className="space-y-1">
              <Label className="text-xs">E-postförhandsgranskning</Label>
              <div 
                className="border border-slate-200 rounded-lg overflow-hidden bg-slate-100 p-2 max-h-[400px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: form.instructions_html || '<p>Ingen historik</p>' }}
              />
            </div>
          ) : (
            <div className="space-y-1">
              <Label className="text-xs">Instruktioner (text) *</Label>
              <Textarea
                className="text-xs font-mono min-h-[200px] leading-relaxed whitespace-pre"
                value={form.instructions}
                onChange={(e) => onFormChange({ ...form, instructions: e.target.value })}
                placeholder="Skriv instruktioner till leverantören..."
                data-testid="order-instructions-input"
              />
            </div>
          )}

          <Button 
            onClick={onSubmit} 
            className="w-full h-9" 
            data-testid="submit-order-button"
          >
            <EnvelopeSimple size={16} className="mr-2" />
            Skicka arbetsorder
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
